import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { CalendarDays, Clock, MapPin, Phone, User, Check, Loader2, PartyPopper, AlertCircle, Euro, Wrench, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';

const LOGO_URL = 'https://static.prod-images.emergentagent.com/jobs/b0feedf7-36df-41e9-9474-99c5d4f77205/images/70da477a4272b0edc6ede2f5e422bf85f189422131684b511c96d6b04c9062b9.jpeg';

const CHECK_GENERALE_BASE = 180;
const USCITA_BASE = 25;
const TARIFFA_ORARIA = 40;
const BLOCCO_ORE_MIN = 1;

export default function BookingLink() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Calendar
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    api.get(`/booking/${token}`)
      .then((r) => {
        setData(r.data);
        if (r.data.status === 'booked') setConfirmed({ already: true });
      })
      .catch(() => setError('Link non valido o scaduto'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!data || data.status === 'booked') return;
    setLoadingSlots(true);
    api.get(`/booking/${token}/slots`)
      .then((r) => setSlots(r.data))
      .catch(() => toast.error('Errore caricamento date'))
      .finally(() => setLoadingSlots(false));
  }, [data, token]);

  const availableDates = slots.map((s) => parseISO(s.date));
  const slotsForDate = slots.find((s) => selectedDate && isSameDay(parseISO(s.date), selectedDate));

  const handleConfirm = async () => {
    if (!selectedDate || !selectedSlot) { toast.error('Seleziona data e orario'); return; }
    setConfirming(true);
    try {
      const res = await api.post(`/booking/${token}/confirm`, {
        date: format(selectedDate, 'yyyy-MM-dd'),
        time_slot: selectedSlot,
      });
      setConfirmed(res.data);
      toast.success('Appuntamento confermato!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore conferma');
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-stone-50">
        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-stone-50">
        <Card className="border-0 shadow-sm rounded-xl max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-base font-bold text-slate-900 mb-2">Link non valido</p>
            <p className="text-sm text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-stone-50" data-testid="booking-confirmed">
        <Card className="border-0 shadow-sm rounded-xl max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <PartyPopper className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-extrabold text-slate-900 mb-2">Appuntamento Confermato!</h2>
            {confirmed.already ? (
              <p className="text-sm text-slate-500">Questo link è già stato utilizzato per una prenotazione.</p>
            ) : (
              <div className="space-y-2 text-sm text-slate-600">
                <p><strong>Data:</strong> {confirmed.date} alle {confirmed.double_slot ? `${confirmed.time_slots[0].split('-')[0]}-${confirmed.time_slots[1].split('-')[1]} (doppio slot)` : confirmed.time_slot}</p>
                <p><strong>Costo stimato:</strong> {confirmed.price}€</p>
                <p className="text-xs text-slate-400 mt-4">Il tecnico verrà all'indirizzo indicato. Il pagamento avviene a fine intervento.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const isCheck = data.service_type === 'check_generale';

  return (
    <div className="min-h-screen bg-stone-50" data-testid="booking-link-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-center">
          <img src={LOGO_URL} alt="REGOLA" className="h-8" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Welcome */}
        <div className="text-center">
          <h1 className="text-xl font-extrabold text-slate-900 mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Ciao {data.nome}!
          </h1>
          <p className="text-sm text-slate-500">
            Scegli il giorno e l'orario per il tuo {isCheck ? 'Check Generale' : 'intervento a chiamata'}
          </p>
        </div>

        {/* Client info summary */}
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-5">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-3">I tuoi dati</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <User className="w-4 h-4 text-blue-500" />
                <span>{data.nome} {data.cognome}</span>
              </div>
              {data.telefono && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Phone className="w-4 h-4 text-blue-500" />
                  <span>{data.telefono}</span>
                </div>
              )}
              {data.address && (
                <div className="flex items-center gap-2 text-slate-600 sm:col-span-2">
                  <MapPin className="w-4 h-4 text-blue-500" />
                  <span>{data.address}</span>
                </div>
              )}
            </div>
            {/* note_admin hidden - visible only to admin/technician */}
          </CardContent>
        </Card>

        {/* Cost Estimate */}
        <Card className="border-0 shadow-sm rounded-xl overflow-hidden" data-testid="cost-estimate">
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-3 flex items-center gap-2">
            <Euro className="w-4 h-4 text-emerald-400" />
            <p className="text-sm font-bold text-white">Idea di Costo</p>
          </div>
          <CardContent className="p-5">
            {isCheck ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <ClipboardCheck className="w-4 h-4 text-blue-500" />
                  <p className="text-sm font-bold text-slate-800">Check Generale</p>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Controllo completo di tutte le finestre e porte: registriamo, controlliamo, sistemiamo e oliamo tutto il possibile.
                </p>
                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  {(() => {
                    const totalFixtures = (data.num_finestre || 0) + (data.num_porte || 0);
                    const isDouble = totalFixtures > 12;
                    const base = isDouble ? CHECK_GENERALE_BASE * 2 : CHECK_GENERALE_BASE;
                    return (
                      <>
                        {isDouble && (
                          <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2" data-testid="bl-double-slot-warning">
                            Oltre 12 serramenti: verranno bloccate 2 fasce orarie consecutive (~3h)
                          </p>
                        )}
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-500">Base check ({totalFixtures} infiss{totalFixtures !== 1 ? 'i' : 'o'}{isDouble ? ', 2 slot' : ''})</span>
                          <span className="font-semibold text-slate-700">€{base.toFixed(0)}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <div>
                            <span className="text-slate-500">Uscita tecnico</span>
                            <p className="text-[10px] text-slate-400 italic">rimborso km EUR 0,50/km andata e ritorno</p>
                          </div>
                          <span className="text-slate-400 italic">variabile</span>
                        </div>
                        <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between">
                          <span className="text-sm font-bold text-slate-800">Totale stimato</span>
                          <span className="text-sm font-extrabold text-emerald-600">a partire da €{base.toFixed(0)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <p className="text-[10px] text-slate-400">
                  Il prezzo finale dipende dalla distanza. Nessun costo nascosto.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <Wrench className="w-4 h-4 text-amber-500" />
                  <p className="text-sm font-bold text-slate-800">Intervento a Chiamata</p>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Intervento mirato per una problematica specifica su finestre o porte.
                </p>
                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between text-xs">
                    <div>
                      <span className="text-slate-500">Uscita tecnico</span>
                      <p className="text-[10px] text-slate-400 italic">rimborso km EUR 0,50/km andata e ritorno</p>
                    </div>
                    <span className="font-semibold text-slate-700">da €{USCITA_BASE}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Manodopera (min. {BLOCCO_ORE_MIN}h x €{TARIFFA_ORARIA}/h)</span>
                    <span className="font-semibold text-slate-700">€{BLOCCO_ORE_MIN * TARIFFA_ORARIA}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Materiali e ricambi</span>
                    <span className="text-slate-400 italic">in base all'intervento</span>
                  </div>
                  <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between">
                    <span className="text-sm font-bold text-slate-800">Totale stimato</span>
                    <span className="text-sm font-extrabold text-emerald-600">a partire da €{USCITA_BASE + BLOCCO_ORE_MIN * TARIFFA_ORARIA}</span>
                  </div>
                </div>
                <p className="text-[10px] text-slate-400">
                  Il tecnico fornirà un preventivo preciso una volta valutato l'intervento. Nessun costo nascosto.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Calendar */}
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays className="w-5 h-5 text-blue-500" />
              <p className="font-bold text-sm text-slate-900">Scegli data e orario</p>
            </div>

            {data.provincia && (
              <div className="bg-blue-50 rounded-lg p-3 mb-4">
                <p className="text-xs text-blue-700">
                  Disponibilità <strong>dal Lunedì al Venerdì</strong> nella provincia di <strong className="capitalize">{data.provincia}</strong>.
                </p>
              </div>
            )}

            {loadingSlots ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(d) => { setSelectedDate(d); setSelectedSlot(''); }}
                  locale={it}
                  disabled={(date) => !availableDates.some((ad) => isSameDay(ad, date))}
                  className="w-full"
                  data-testid="booking-calendar"
                />
                <div>
                  {selectedDate ? (
                    <div className="space-y-3">
                      <p className="text-sm font-bold text-slate-900">
                        {format(selectedDate, 'EEEE d MMMM', { locale: it })}
                      </p>
                      {slotsForDate?.slots?.map((slot) => (
                        <div
                          key={slot}
                          onClick={() => setSelectedSlot(slot)}
                          className={`p-3 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-3 ${selectedSlot === slot ? 'border-blue-500 bg-blue-50' : 'border-slate-100 hover:border-slate-200'}`}
                          data-testid={`bslot-${slot}`}
                        >
                          <Clock className="w-4 h-4 text-blue-500" />
                          <span className="text-sm font-medium text-slate-700">{slotsForDate?.double_slot ? ({ '08:00-10:00': '08:00-12:00 (2 fasce)', '13:00-15:00': '13:00-17:00 (2 fasce)' }[slot] || slot) : slot}</span>
                          {selectedSlot === slot && <Check className="w-4 h-4 text-blue-600 ml-auto" />}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-sm text-slate-400">
                      Seleziona un giorno dal calendario
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Confirm */}
        {selectedDate && selectedSlot && (
          <Button
            className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full py-6 text-base font-semibold"
            onClick={handleConfirm}
            disabled={confirming}
            data-testid="confirm-booking-btn"
          >
            {confirming ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Conferma in corso...</>
            ) : (
              <><Check className="w-4 h-4 mr-2" /> Conferma Appuntamento — {format(selectedDate, 'd MMMM', { locale: it })} {selectedSlot}</>
            )}
          </Button>
        )}

        <p className="text-xs text-center text-slate-400">
          Il pagamento avviene direttamente al tecnico a fine intervento (contanti, POS o bonifico).
        </p>
      </main>
    </div>
  );
}
